import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { User, Lock, Shield, Eye, EyeOff, Check, X, AlertTriangle, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Card, Button, FormField, Tabs, Alert, Badge, Modal } from '../components/UI';
import { format, parseISO } from 'date-fns';

const ROLE_DESCRIPTIONS = {
  employee: 'Can view and submit reports assigned to them.',
  supervisor: 'Can create/manage reports for their department and view department dashboards.',
  manager: 'Can view organisation-wide dashboards and analytics.',
  admin: 'Full access: user management, audit logs, all reports and settings.',
};

function PasswordStrength({ password }) {
  const checks = [
    { label: 'At least 12 characters', pass: password.length >= 12 },
    { label: 'Uppercase letter', pass: /[A-Z]/.test(password) },
    { label: 'Lowercase letter', pass: /[a-z]/.test(password) },
    { label: 'Number', pass: /[0-9]/.test(password) },
    { label: 'Special character', pass: /[^A-Za-z0-9]/.test(password) },
  ];
  const score = checks.filter(c => c.pass).length;
  const colors = ['var(--danger)', 'var(--danger)', 'var(--warning)', 'var(--warning)', 'var(--success)'];
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  if (!password) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 99, background: i <= score ? colors[score - 1] : 'var(--border)', transition: 'background .3s' }} />
        ))}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: score > 0 ? colors[score - 1] : 'var(--text-muted)', marginBottom: 8 }}>
        {score > 0 ? labels[score - 1] : ''}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {checks.map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12 }}>
            {c.pass ? <Check size={12} color="var(--success)" /> : <X size={12} color="var(--text-subtle)" />}
            <span style={{ color: c.pass ? 'var(--success)' : 'var(--text-muted)' }}>{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'profile');
  const [profile, setProfile] = useState({ username: '', full_name: '', email: '' });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPwd, setShowPwd] = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [profileMsg, setProfileMsg] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) setProfile({ username: user.username || '', full_name: user.full_name || '', email: user.email || '' });
  }, [user]);

  useEffect(() => {
    setSearchParams(tab !== 'profile' ? { tab } : {});
  }, [tab, setSearchParams]);

  async function saveProfile(e) {
    e.preventDefault();
    const errs = {};
    if (!profile.username.trim()) errs.username = 'Username is required.';
    if (!profile.full_name.trim()) errs.full_name = 'Full name is required.';
    if (!profile.email.trim()) errs.email = 'Email is required.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await api.put('/auth/profile', profile);
      updateUser({ username: profile.username, full_name: profile.full_name, email: profile.email });
      toast.success('Profile updated!');
      setProfileMsg('Profile saved successfully.');
    } catch(err) { toast.error(err.response?.data?.error || 'Update failed.'); }
    setLoading(false);
  }

  async function changePassword(e) {
    e.preventDefault();
    const errs = {};
    if (!passwords.currentPassword) errs.currentPassword = 'Current password is required.';
    if (passwords.newPassword.length < 12) errs.newPassword = 'Password must be at least 12 characters.';
    else if (!/[A-Z]/.test(passwords.newPassword)) errs.newPassword = 'Must contain uppercase.';
    else if (!/[0-9]/.test(passwords.newPassword)) errs.newPassword = 'Must contain a number.';
    else if (!/[^A-Za-z0-9]/.test(passwords.newPassword)) errs.newPassword = 'Must contain a special character.';
    if (passwords.newPassword !== passwords.confirmPassword) errs.confirmPassword = 'Passwords do not match.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await api.put('/auth/change-password', { currentPassword: passwords.currentPassword, newPassword: passwords.newPassword });
      toast.success('Password changed successfully!');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch(err) {
      const msg = err.response?.data?.error || 'Change failed.';
      toast.error(msg);
      setErrors({ currentPassword: msg });
    }
    setLoading(false);
  }

  function setP(k, v) { setPasswords(p => ({ ...p, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); }

  function closeDeleteModal() {
    setDeleteOpen(false);
    setDeletePassword('');
    setDeleteConfirmText('');
    setDeleteError('');
  }

  async function deleteAccount(e) {
    e.preventDefault();
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      setDeleteError('Type DELETE to confirm.');
      return;
    }
    if (!deletePassword) {
      setDeleteError('Enter your password to confirm.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await api.delete('/auth/account', { data: { password: deletePassword } });
      toast.success('Your account has been deleted.');
      logout();
      navigate('/login');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to delete account.');
    }
    setDeleting(false);
  }

  return (
    <div style={{ padding: '32px 36px', maxWidth: 780, margin: '0 auto' }} className="fade-in">
      <PageHeader title="Settings" subtitle="Manage your profile, security, and preferences" />

      <Tabs
        tabs={[
          { id: 'profile', label: 'Profile', icon: User },
          { id: 'password', label: 'Password', icon: Lock },
          { id: 'security', label: 'Security', icon: Shield },
          { id: 'danger', label: 'Danger Zone', icon: AlertTriangle },
        ]}
        active={tab} onChange={t => { setTab(t); setErrors({}); setProfileMsg(''); }}
      />

      {tab === 'profile' && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28, padding: '16px 20px', background: 'var(--primary-ghost)', borderRadius: 'var(--radius-md)', border: '1.5px solid rgba(27,63,148,.15)' }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
              {(user?.full_name || user?.username || '?').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{user?.full_name || user?.username}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{user?.email}</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                <Badge label={user?.role} type={user?.role} />
                {user?.department_name && <Badge label={user.department_name} type="info" />}
              </div>
            </div>
          </div>

          {profileMsg && <Alert type="success" message={profileMsg} onClose={() => setProfileMsg('')} />}

          <form onSubmit={saveProfile}>
            <FormField label="Username" required error={errors.username} hint="Used to log in. Must be unique.">
              <input value={profile.username} onChange={e => { setProfile(p => ({ ...p, username: e.target.value })); setErrors(er => ({ ...er, username: '' })); }} placeholder="username" />
            </FormField>
            <FormField label="Full Name" required error={errors.full_name}>
              <input value={profile.full_name} onChange={e => { setProfile(p => ({ ...p, full_name: e.target.value })); setErrors(er => ({ ...er, full_name: '' })); }} placeholder="Your full name" />
            </FormField>
            <FormField label="Email Address" required error={errors.email}>
              <input type="email" value={profile.email} onChange={e => { setProfile(p => ({ ...p, email: e.target.value })); setErrors(er => ({ ...er, email: '' })); }} placeholder="your@email.com" />
            </FormField>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Button type="submit" icon={User} loading={loading}>Save Profile</Button>
            </div>
          </form>
        </Card>
      )}

      {tab === 'password' && (
        <Card>
          {user?.must_change_password && (
            <Alert type="warning" message="You are required to change your password before continuing." />
          )}
          <form onSubmit={changePassword}>
            <FormField label="Current Password" required error={errors.currentPassword}>
              <div style={{ position: 'relative' }}>
                <input type={showPwd.current ? 'text' : 'password'} value={passwords.currentPassword} onChange={e => setP('currentPassword', e.target.value)} placeholder="Enter current password" style={{ paddingRight: 42 }} />
                <button type="button" onClick={() => setShowPwd(s => ({ ...s, current: !s.current }))} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}>
                  {showPwd.current ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </FormField>
            <FormField label="New Password" required error={errors.newPassword}>
              <div style={{ position: 'relative' }}>
                <input type={showPwd.new ? 'text' : 'password'} value={passwords.newPassword} onChange={e => setP('newPassword', e.target.value)} placeholder="Create a strong password" style={{ paddingRight: 42 }} />
                <button type="button" onClick={() => setShowPwd(s => ({ ...s, new: !s.new }))} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}>
                  {showPwd.new ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <PasswordStrength password={passwords.newPassword} />
            </FormField>
            <FormField label="Confirm New Password" required error={errors.confirmPassword}>
              <div style={{ position: 'relative' }}>
                <input type={showPwd.confirm ? 'text' : 'password'} value={passwords.confirmPassword} onChange={e => setP('confirmPassword', e.target.value)} placeholder="Re-enter new password" style={{ paddingRight: 42 }} />
                <button type="button" onClick={() => setShowPwd(s => ({ ...s, confirm: !s.confirm }))} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}>
                  {showPwd.confirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {passwords.confirmPassword && passwords.newPassword === passwords.confirmPassword && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--success)' }}><Check size={12} /> Passwords match</div>
              )}
            </FormField>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Button type="submit" icon={Lock} loading={loading}>Change Password</Button>
            </div>
          </form>
        </Card>
      )}

      {tab === 'security' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>Account Information</h3>
            {[
              ['Role', <Badge label={user?.role} type={user?.role} />],
              ['Department', user?.department_name || 'Not assigned'],
              ['Account Created', user?.created_at ? format(parseISO(user.created_at), 'MMMM d, yyyy') : '—'],
              ['Last Login', user?.last_login ? format(parseISO(user.last_login), 'MMMM d, yyyy h:mm a') : 'Never'],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>{l}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{v}</span>
              </div>
            ))}
          </Card>

          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>Your Role Permissions</h3>
            <div style={{ padding: '14px 16px', background: 'var(--primary-ghost)', borderRadius: 10, borderLeft: '4px solid var(--primary)' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--primary)', marginBottom: 6, textTransform: 'capitalize' }}>{user?.role}</div>
              <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{ROLE_DESCRIPTIONS[user?.role] || '—'}</p>
            </div>
          </Card>

          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>Platform Security Features</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['JWT Authentication', 'Tokens expire after 8 hours'],
                ['Account Lockout', 'After 5 failed attempts (30 min lock)'],
                ['Password History', 'Last 5 passwords cannot be reused'],
                ['Rate Limiting', 'Login attempts are rate-limited globally'],
                ['Audit Logging', 'All actions are logged with IP and timestamp'],
                ['Role-Based Access Control', 'Enforced on every API endpoint'],
                ['Secure File Uploads', 'Extension whitelist + size limits (10MB)'],
                ['Content Security Policy', 'Helmet.js CSP headers on all responses'],
              ].map(([title, desc]) => (
                <div key={title} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <Check size={12} color="#fff" />
                  </div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === 'danger' && (
        <Card style={{ border: '1.5px solid rgba(220,38,38,.25)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(220,38,38,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <AlertTriangle size={18} color="var(--danger)" />
            </div>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Delete Account</h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.6 }}>
                Permanently delete your account and all associated login access. This cannot be undone.
                If your account has submitted reports or other linked records, deletion may be blocked —
                contact an administrator in that case.
              </p>
            </div>
          </div>
          <Button icon={Trash2} variant="danger" onClick={() => setDeleteOpen(true)}>
            Delete My Account
          </Button>
        </Card>
      )}

      <Modal
        open={deleteOpen}
        onClose={deleting ? () => {} : closeDeleteModal}
        title="Delete Account"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={closeDeleteModal} disabled={deleting}>Cancel</Button>
            <Button onClick={deleteAccount} loading={deleting} icon={Trash2} variant="danger">
              Permanently Delete
            </Button>
          </>
        }
      >
        <Alert type="warning" message="This action is permanent and cannot be undone. You will be logged out immediately." />
        <form onSubmit={deleteAccount}>
          <FormField label={`Type "DELETE" to confirm`} required error={deleteConfirmText && deleteConfirmText.trim().toUpperCase() !== 'DELETE' ? '' : ''}>
            <input value={deleteConfirmText} onChange={e => { setDeleteConfirmText(e.target.value); setDeleteError(''); }} placeholder="DELETE" />
          </FormField>
          <FormField label="Confirm Your Password" required error={deleteError}>
            <input type="password" value={deletePassword} onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }} placeholder="Enter your password" />
          </FormField>
        </form>
      </Modal>
    </div>
  );
}
